import React from 'react';
import { useNavigate } from 'react-router-dom';

const AccountSettingsView = ({
  userData,
  loading,
  error,
  isAdvancedOpen,
  subscription,
  planDetails,
  systemVersion,
  isPortalLoading,
  portalError,
  handleSubscriptionManage,
  handleUpgradeToBusiness,
  toggleAdvanced,
  handleOpenProfileEdit
}) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = React.useState(false);
  
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-400">Loading account data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-black">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    );
  }

  // Determine the badge colors based on plan type
  const getBadgeClasses = () => {
    switch (planDetails.planType) {
      case 'pro':
        return 'bg-gradient-to-r from-blue-600 to-purple-600';
      case 'business':
        return 'bg-gradient-to-r from-yellow-500 to-orange-500';
      default:
        return 'bg-gray-700';
    }
  };

  // Format date to local string
  const formatDate = (date) => {
    if (!date) return 'Not available';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Helper to format subscription status for display
  const formatStatus = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'trialing': return 'Trial';
      case 'canceled': return 'Canceled';
      case 'past_due': return 'Past Due';
      case 'unpaid': return 'Unpaid';
      case 'incomplete': return 'Incomplete';
      case 'incomplete_expired': return 'Expired';
      case 'none': return 'None';
      default: return status || 'Unknown';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6 md:p-10">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-2">Settings</h1>
        <p className="text-gray-400 text-lg mb-10">
          Manage your account, billing, and team settings
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Profile Section */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 shadow-xl flex flex-col items-center text-center">
              {userData?.photoURL && typeof userData.photoURL === 'string' && userData.photoURL.trim() !== '' && !imageError ? (
  <div className="w-24 h-24 mb-6 rounded-full overflow-hidden border-4 border-blue-500">
    <img
      src={userData.photoURL}
      alt=""
      className="w-full h-full object-cover"
      loading="lazy"
      onError={() => setImageError(true)}
    />
  </div>
) : (
  <div className="w-24 h-24 mb-6 rounded-full bg-teal-500 flex items-center justify-center border-4 border-blue-700">
    <span className="text-5xl font-bold text-white select-none">
      {userData?.displayName?.charAt(0)?.toUpperCase() || userData?.email?.charAt(0)?.toUpperCase() || 'U'}
    </span>
  </div>
)}

              
              <h2 className="text-2xl font-bold mb-1">{userData?.displayName || 'User'}</h2>
              <p className="text-gray-400 text-sm mb-2">{userData?.email}</p>
              
              {userData?.phoneNumber && (
                <p className="text-gray-400 text-sm mb-6 flex items-center justify-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {userData.phoneNumber}
                </p>
              )}
              
              {!userData?.phoneNumber && (
                <p className="text-gray-500 text-xs mb-6">No phone number</p>
              )}
              
              <button 
                className="bg-transparent border border-gray-600 w-full py-2 rounded-lg hover:bg-gray-800 transition mb-4 text-sm flex items-center justify-center gap-2"
                onClick={handleOpenProfileEdit}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit Profile
              </button>
            </div>
          </div>

          {/* Account & Security Section */}
          <div className="lg:col-span-2 space-y-8">
            {/* Subscription Card */}
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Subscription</h2>
                <span className={`${getBadgeClasses()} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                  {planDetails.planName.toUpperCase()}
                  {(planDetails.subscriptionStatus !== 'active' && planDetails.subscriptionStatus !== 'trialing' && planDetails.subscriptionStatus !== 'none') && 
                    ` (${formatStatus(planDetails.subscriptionStatus)})`
                  }
                </span>
              </div>
              
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                {subscription ? (
                  <button 
                    onClick={handleSubscriptionManage}
                    disabled={isPortalLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 transition px-6 py-3 rounded-lg font-medium text-center flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    aria-label="Open subscription management portal"
                  >
                    {isPortalLoading ? (
                      <span className="flex items-center">
                        <span className="mr-2 h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                        <span>Loading portal...</span>
                      </span>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        Manage Subscription
                      </>
                    )}
                  </button>
                ) : (
                  <button 
                    onClick={() => navigate('/pricing')}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 transition px-6 py-3 rounded-lg font-medium text-center flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Upgrade to Pro
                  </button>
                )}
                
                {planDetails.showUpgradeOption && subscription && planDetails.planType !== 'business' && (
                  <button 
                    onClick={handleUpgradeToBusiness}
                    className="flex-1 bg-gray-800 hover:bg-gray-700 transition px-6 py-3 rounded-lg font-medium text-center flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    Upgrade to Business
                  </button>
                )}
              </div>

              {portalError && (
                <div className="mt-2 text-red-500 text-sm">
                  {portalError}
                </div>
              )}
              
              <div className="border-t border-gray-800 pt-6">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-400">Current Plan</span>
                  <span className="font-medium">{planDetails.planName} Plan</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-400">Status</span>
                  <span className={`font-medium ${planDetails.subscriptionStatus === 'active' || planDetails.subscriptionStatus === 'trialing' ? 'text-green-400' : 'text-yellow-400'}`}>
                    {formatStatus(planDetails.subscriptionStatus)}
                  </span>
                </div>
                {(planDetails.subscriptionStatus === 'active' || planDetails.subscriptionStatus === 'trialing') && (
                  <>
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-400">Billing Cycle</span>
                      <span className="font-medium">{planDetails.billingCycle || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Next Payment</span>
                      <span className="font-medium">{formatDate(planDetails.nextPaymentDate)}</span>
                    </div>
                  </>
                )}
                {(planDetails.subscriptionStatus !== 'active' && planDetails.subscriptionStatus !== 'trialing' && planDetails.subscriptionStatus !== 'none') && (
                  <div className="mt-4 text-sm text-yellow-400">
                    Your {planDetails.planName} plan is currently {formatStatus(planDetails.subscriptionStatus)}.
                  </div>
                )}
                {planDetails.subscriptionStatus === 'none' && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Features</span>
                    <span className="font-medium">Limited access</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Advanced Settings Card */}
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 shadow-xl">
              <button 
                onClick={toggleAdvanced}
                className="w-full flex items-center justify-between text-left mb-2"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <h2 className="text-2xl font-bold">Advanced Settings</h2>
                </div>
                <svg 
                  className={`w-5 h-5 transform transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isAdvancedOpen && (
                <div className="mt-6 space-y-4 animate-fadeIn">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-gray-800">
                      <p className="text-gray-400 text-sm mb-1">Account ID</p>
                      <p className="font-mono text-sm break-all">{userData?.uid}</p>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-gray-800">
                      <p className="text-gray-400 text-sm mb-1">Created At</p>
                      <p>{userData?.createdAt ? new Date(userData.createdAt.seconds * 1000).toLocaleDateString() : 'Not available'}</p>
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-gray-800">
                    <p className="text-gray-400 text-sm mb-1">Application Version</p>
                    <p className="font-mono text-sm">{systemVersion || 'Not available'}</p>
                  </div>
                  
                  <div className="pt-4">
                    <button className="flex items-center gap-2 text-red-500 hover:text-red-400 transition">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Account
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsView; 